import { useState, useRef, useEffect } from "react"; 
import { 
    LOCATION_CONFIG, 
    LOCATION_LIST, 
    TIME_OF_DAY, 
    SEASON, 
    VIEW_MODE, 
    v_Drone,   
    v_Cyclist,
    a_Drone,
    a_Cyclist, 
    DEFAULT_FADE_DURATION_MS, 
    MAP_IMAGE_SRC 
} from "../SceneComponents/VideoConfig.jsx";
import { FullScreenButton } from "./FullScreenButton.jsx";
import { playClickSound } from './SoundEffect';

export function ViewControlsBar({ sceneMedia, handleEnvironmentSwitch, isTransitioning }) {
    
    const currentViewMode = sceneMedia.viewMode || VIEW_MODE.PEDESTRIAN;
    
    const [isTimeHovered, setIsTimeHovered] = useState(false);
    const [isSeasonHovered, setIsSeasonHovered] = useState(false);
    const [isViewModeHovered, setIsViewModeHovered] = useState(false);

    const toggleTimeOfDay = () => {
        const newTime = sceneMedia.time === TIME_OF_DAY.MORNING ? TIME_OF_DAY.EVENING : TIME_OF_DAY.MORNING;
        const targetSeason = currentViewMode !== VIEW_MODE.PEDESTRIAN ? SEASON.SPRING : sceneMedia.season;
        
        const newMedia = LOCATION_CONFIG[sceneMedia.locId].media[targetSeason][newTime];
        handleEnvironmentSwitch(newMedia);
        playClickSound();
    };

    const toggleSeason = () => {
        const newSeason = sceneMedia.season === SEASON.SPRING ? SEASON.AUTUMN : SEASON.SPRING;
        const targetTime = currentViewMode !== VIEW_MODE.PEDESTRIAN ? TIME_OF_DAY.MORNING : sceneMedia.time;
        
        const newMedia = LOCATION_CONFIG[sceneMedia.locId].media[newSeason][targetTime];
        handleEnvironmentSwitch(newMedia);
        playClickSound();
    };

    const cycleViewMode = () => {
        playClickSound();
        if (currentViewMode === VIEW_MODE.PEDESTRIAN) {
            handleEnvironmentSwitch({
                ...sceneMedia, 
                viewMode: VIEW_MODE.BIRD,
                videoSrc: v_Drone, 
                audioSrc: a_Drone
            });
        } 
        else if (currentViewMode === VIEW_MODE.BIRD) {
            handleEnvironmentSwitch({
                ...sceneMedia,
                viewMode: VIEW_MODE.BIKE,
                videoSrc: v_Cyclist, 
                audioSrc: a_Cyclist
            });
        } 
        else {
            const resetMedia = LOCATION_CONFIG[sceneMedia.locId].media[SEASON.SPRING][TIME_OF_DAY.MORNING];
            handleEnvironmentSwitch({
                ...resetMedia,
                viewMode: VIEW_MODE.PEDESTRIAN
            });
        }
    };

    const getHoverImagePath = (basePath, isHovered) => {
        return isHovered ? basePath.replace('.png', '_Glow.png') : basePath;
    };

    const baseTimeIcon = sceneMedia.time === TIME_OF_DAY.EVENING ? "/images/ui/T_Icons_Cycle/T_Icon_Sunset_Cycle.png" : "/images/ui/T_Icons_Cycle/T_Icon_Sunrise_Cycle.png"; 
    const timeIcon = getHoverImagePath(baseTimeIcon, isTimeHovered);

    const baseSeasonIcon = sceneMedia.season === SEASON.AUTUMN ? "/images/ui/T_Icons_Cycle/T_Icon_Autumn_Cycle.png" : "/images/ui/T_Icons_Cycle/T_Icon_Springtime_Cycle.png"; 
    const seasonIcon = getHoverImagePath(baseSeasonIcon, isSeasonHovered);
    
    let baseViewModeIcon = "/images/ui/T_Icons_Cycle/T_Icon_Pedestrian_Cycle.png"; 
    if (currentViewMode === VIEW_MODE.BIRD) baseViewModeIcon = "/images/ui/T_Icons_Cycle/T_Icon_Bird_Cycle.png"; 
    if (currentViewMode === VIEW_MODE.BIKE) baseViewModeIcon = "/images/ui/T_Icons_Cycle/T_Icon_Bike_Cycle.png"; 
    const viewModeIcon = getHoverImagePath(baseViewModeIcon, isViewModeHovered);

    return (
        <div className="view-controls-pill">
            <button 
                type="button" 
                className="pill-icon-button" 
                onClick={toggleTimeOfDay} 
                title="Time of Day" 
                disabled={isTransitioning}
                onMouseEnter={() => setIsTimeHovered(true)} 
                onMouseLeave={() => setIsTimeHovered(false)}
            >    
                <img src={timeIcon} alt="Toggle Time" />
            </button>

            <button 
                type="button" 
                className="pill-icon-button" 
                onClick={toggleSeason} 
                title="Season" 
                disabled={isTransitioning}
                onMouseEnter={() => setIsSeasonHovered(true)} 
                onMouseLeave={() => setIsSeasonHovered(false)}
            >
                <img src={seasonIcon} alt="Toggle Season" />
            </button>

            <button 
                type="button" 
                className="pill-icon-button" 
                onClick={cycleViewMode} 
                title="View Mode" 
                disabled={isTransitioning}
                onMouseEnter={() => setIsViewModeHovered(true)} 
                onMouseLeave={() => setIsViewModeHovered(false)}
            >
                <img src={viewModeIcon} alt="Cycle View Mode" />
            </button>
        </div>
    );
}

export function UI({ sceneMedia, setSceneMedia }) {
    
    const [isReady, setIsReady] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);    

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsReady(true);
            console.debug("UI is now ready and visible.");
        }, DEFAULT_FADE_DURATION_MS);
        return () => clearTimeout(timer);
    }, []);
    
    const handleEnvironmentSwitch = (newSceneMedia) => {
        setIsTransitioning(true);
        playClickSound(); 
        
        if (isTransitioning) {
            console.debug("Double click ignored: Transition already in progress.");
            return; 
        }

        console.debug(`Switching to: Location ${newSceneMedia.locId} | ${newSceneMedia.season} | ${newSceneMedia.time}`);
        
        setSceneMedia(prev => ({
            ...newSceneMedia,        
            audioSrc: prev.audioSrc  
        }));

        setTimeout(() => {
            setSceneMedia(newSceneMedia);
            setTimeout(() => {
                setIsTransitioning(false);
            }, DEFAULT_FADE_DURATION_MS);
        }, DEFAULT_FADE_DURATION_MS);
    };

    return (
        <div 
            className="ui-container"
            style={{
                opacity: (isReady && !isTransitioning) ? 1 : 0,        
                transition: 'opacity 0.4s ease-in-out' 
            }}
        >
            <div className="util-buttons">
                <FullScreenButton />
            </div>
            
            <div className="top-center-map-overlay">
                <img src={MAP_IMAGE_SRC} alt="Tribschenstrasse Map" className="map-overlay-graphic" />
                
                {LOCATION_LIST.map((loc) => (
                    <button
                        key={loc.id}
                        className={`ui-map-hotspot ${sceneMedia.locId === loc.id ? 'active' : ''}`}
                        style={{ top: loc.top, left: loc.left }}
                        onClick={() => {
                            const defaultMedia = LOCATION_CONFIG[loc.id].media[loc.defaultSeason][loc.defaultTime];
                            handleEnvironmentSwitch(defaultMedia);
                        }}
                    >
                        {sceneMedia.locId === loc.id && <span className="active-point-ring" />}
                    </button>
                ))}
            </div>
            
            <div className="view-controls-wrapper">
                 <ViewControlsBar 
                     sceneMedia={sceneMedia} 
                     isTransitioning={isTransitioning}
                     handleEnvironmentSwitch={handleEnvironmentSwitch} 
                 />
            </div>
        </div>
    );
}